import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";

export default function EmailsScheduled() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (status) params.status = status;
      const { data } = await adminHttp.get("/admin/emails/scheduled", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar este envío programado?")) return;
    try {
      await adminHttp.post(`/admin/emails/scheduled/${id}/cancel`);
      toast.success("Envío cancelado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="emails-scheduled-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="divider-accent" /><span className="kicker">Scheduler</span>
      </div>
      <h1 className="font-display font-black tracking-tightest text-4xl md:text-5xl">Emails programados</h1>
      <p className="mt-3 text-white/55 max-w-2xl">Jobs de nurturing y seguimiento. Se cancelan automáticamente cuando el lead cambia de status.</p>

      <div className="mt-8 flex gap-2">
        {["pending", "sent", "cancelled", "failed", ""].map((s) => (
          <button key={s || "all"} onClick={() => { setStatus(s); setPage(1); }}
                  className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest border ${status === s ? "bg-accent text-[#1F2937] border-accent" : "border-[#DDE2D5] text-[#374151] hover:border-accent"}`}
                  data-testid={`sched-filter-${s || 'all'}`}>
            {s || "Todos"}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-[#DDE2D5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F2F4ED] text-[#6B7280] font-mono text-[10px] uppercase tracking-widest">
              <th className="text-left px-6 py-3">Programado para</th>
              <th className="text-left px-6 py-3">Plantilla</th>
              <th className="text-left px-6 py-3">Destinatario</th>
              <th className="text-left px-6 py-3">Lead</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-right px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="emails-scheduled-table">
            {loading ? (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6B7280] font-mono text-xs">Cargando…</td></tr>
            ) : items.map((j) => (
              <tr key={j.id} className="border-t border-[#EAECEA] hover:bg-[#F2F4ED]/50">
                <td className="px-6 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-[#1F2937]">
                    <Clock className="w-4 h-4 text-accent" />
                    <span className="font-mono text-[11px]">{format(parseISO(j.scheduled_for), "d MMM yyyy · HH:mm", { locale: es })}</span>
                  </div>
                </td>
                <td className="px-6 py-3 font-mono text-[11px] text-accent">{j.template_key}</td>
                <td className="px-6 py-3 text-white/75 truncate max-w-[220px]">{(j.to || []).join(", ")}</td>
                <td className="px-6 py-3">
                  {j.lead_id ? (
                    <Link to={`/admin/leads/${j.lead_id}`} className="text-accent font-mono text-[11px] hover:underline">{j.lead_id.slice(0, 8)}</Link>
                  ) : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-6 py-3"><StatusBadge status={j.status} /></td>
                <td className="px-6 py-3 text-right">
                  {j.status === "pending" && (
                    <button onClick={() => cancel(j.id)} className="p-2 text-[#4B5563] hover:text-red-400" title="Cancelar" data-testid={`sched-cancel-${j.id}`}>
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6B7280] font-mono text-xs">Sin jobs para este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-[#6B7280]">Total: {total} · Página {page}/{pages}</p>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="px-4 py-2 border border-[#DDE2D5] text-[#374151] text-xs font-mono uppercase disabled:opacity-40 hover:border-accent">Anterior</button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)}
            className="px-4 py-2 border border-[#DDE2D5] text-[#374151] text-xs font-mono uppercase disabled:opacity-40 hover:border-accent">Siguiente</button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "#1e4d29", text: "#7fff9f" }, failed: { bg: "#4d1e1e", text: "#ff7f7f" },
    queued: { bg: "#1a1a2e", text: "#8a8aff" }, skipped: { bg: "#2e2e2e", text: "#aaa" },
    pending: { bg: "#1a1a2e", text: "#8a8aff" }, cancelled: { bg: "#2e2e2e", text: "#888" },
    running: { bg: "#4d3f1e", text: "#ffd97f" },
  };
  const c = map[status] || { bg: "#2e2e2e", text: "#aaa" };
  return <span style={{ background: c.bg, color: c.text }} className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider">{status}</span>;
}
